import { ConversationTypeEnum } from "@coxauto-ui/cxm/communications/interfaces";
import { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { ConversationContext, ConversationDetailsContext } from "../context";
import { replyAreaTestId } from "../testIds";
import MessageSendUnavailable from "./message-send-unavailable";
import { ReplySlideOutGlobalStyles } from "./reply-slideout-styles";
import { StationeryOptions } from "@coxauto-ui/cxm/communications/api";
import { ReplyTypeSwitch } from "./reply-types/reply-type-switch";

interface ReplyAppProps {
  stationeryOptions: StationeryOptions;
}

const StyledReplyApp = styled.div<{
  conversationType: ConversationTypeEnum | undefined;
  hidden: boolean;
}>`
  border-radius: 5px;
  padding: 0.5em;
  .control-label {
    color: #42454a;
  }

  display: ${props => props.hidden && "none"};

  /* Alert width constraints to prevent horizontal scroll */
  .MuiAlert-root,
  .alert,
  [role="alert"] {
    max-width: 100%;
    width: auto !important;
    box-sizing: border-box;
  }
  .MuiAlert-action {
    max-width: 100% !important;
    width: auto !important;
    box-sizing: border-box !important;
    flex-shrink: 1 !important;
    min-width: 0 !important;
    overflow: hidden !important;
  }
`;

export function ReplyApp({ stationeryOptions }: ReplyAppProps) {
  const [messageFailedToSend, setMessageFailedToSend] = useState(false);
  const [messageSentResponseFailedMsg, setMessageSentResponseFailedMsg] =
    useState("");
  const [isTextAndNoPhoneNumber, setIsTextAndNoPhoneNumber] = useState(false);
  const [isTextWithRepairOrderOnly, setIsTextWithRepairOrderOnly] =
    useState(false);

  const {
    replyOptions,
    disableReply,
    dealerCommunicationDetails,
    autoLeadId,
    repairOrderId
  } = useContext(ConversationContext);

  const {
    currentConversationType,
    selectedReplyOption,
    setSelectedReplyOption
  } = useContext(ConversationDetailsContext);

  useEffect(() => {
    const selectedOption = replyOptions.find(
      option => option.type === currentConversationType?.valueOf()
    );
    setSelectedReplyOption(selectedOption || null);
  }, [currentConversationType, replyOptions, setSelectedReplyOption]);

  useEffect(() => {
    if (
      currentConversationType === ConversationTypeEnum.TEXT &&
      dealerCommunicationDetails?.userPhoneNumbers.length === 0 &&
      dealerCommunicationDetails?.dealerPhoneNumber.phoneNumber.length === 0
    ) {
      setIsTextAndNoPhoneNumber(true);
    } else {
      setIsTextAndNoPhoneNumber(false);
    }
  }, [currentConversationType, dealerCommunicationDetails]);

  useEffect(() => {
    if (
      currentConversationType === ConversationTypeEnum.TEXT &&
      repairOrderId &&
      !autoLeadId
    ) {
      setIsTextWithRepairOrderOnly(true);
    } else {
      setIsTextWithRepairOrderOnly(false);
    }
  }, [currentConversationType, repairOrderId, autoLeadId]);

  const msgSendFailedUpdateMsg = (msg: string) => {
    const getComplianceError = () => {
      return `Customer has opted out of receiving ${currentConversationType} messages.`;
    };

    if (msg === "Compliance Issue") {
      setMessageSentResponseFailedMsg(getComplianceError());
      return;
    }
    setMessageSentResponseFailedMsg(msg);
  };

  if (disableReply)
    return (
      <MessageSendUnavailable
        textMessage={'Send function is Unavailable in "View as" Mode'}
        title={"View As"}
      />
    );

  return (
    <>
      <ReplySlideOutGlobalStyles />
      <StyledReplyApp
        conversationType={currentConversationType || undefined}
        data-testid={replyAreaTestId}
        hidden={
          !currentConversationType ||
          currentConversationType === ConversationTypeEnum.ALL
        }
        id="conversation-list-reply-pane"
      >
        <ReplyTypeSwitch
          isTextAndNoPhoneNumber={isTextAndNoPhoneNumber}
          isTextWithRepairOrderOnly={isTextWithRepairOrderOnly}
          messageFailedToSend={messageFailedToSend}
          messageSentResponseFailedMsg={messageSentResponseFailedMsg}
          selectedReplyOption={selectedReplyOption}
          stationeryOptions={stationeryOptions}
          onMessageSendFailed={() => {
            setMessageFailedToSend(true);
          }}
          onMessageSendFailedSetMsg={msgSendFailedUpdateMsg}
        />
      </StyledReplyApp>
    </>
  );
}

export default ReplyApp;
